import { message } from "antd";

// Small shared bits for the server Show screen. The Content tab and the
// metadata block both render opaque technical values (UUIDs, crafty ids,
// IP:port) that need truncating and a copy affordance.

export function copyToClipboard(value: string, label = 'Copied') {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(value);
  } else {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  message.success(label);
}

/** `CB0474E6-6C0B-42A5-A004-F219A6915E2D` → `CB0474E6…6915E2D` */
export function truncateMiddle(value: string, head: number, tail: number) {
  if (!value || value.length <= head + tail + 1) return value;

  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

/** Compact relative time for dense rows — `3m ago`, `2h ago`. */
export function shortFromNow(iso: string | null | undefined) {
  if (!iso) return null;

  const time = new Date(iso).getTime();
  if (isNaN(time)) return null;

  const seconds = Math.round((Date.now() - time) / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  return `${Math.floor(hours / 24)}d ago`;
}

export const megabytes = (bytes: number | undefined) =>
  bytes === undefined || bytes === null ? '-' : `${Math.round(bytes / 1024 / 1024)} MB`;

export const gigabytes = (bytes: number | undefined) =>
  bytes === undefined || bytes === null ? '-' : `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;

// The part of GET /onesky/servers/versions the pickers read; `stable`
// comes newest first.
export interface BedrockVersions {
  latest?: string;
  stable: string[];
}

// `latest` always comes first — Bedrock clients auto-update and cannot
// join an older server, so pinning a build only makes sense for an addon
// that breaks on newer ones.
export function bedrockVersionOptions(versions: BedrockVersions) {
  return [
    { value: 'latest', label: versions.latest ? `latest (${versions.latest})` : 'latest' },
    ...versions.stable
      .filter(version => version !== 'latest')
      .map(version => ({ value: version, label: version }))
  ];
}

/** Lifetime playtime is reported in seconds and runs to hundreds of hours. */
export const playtimeHours = (seconds: number | undefined) => {
  if (!seconds) return '0h';

  const hours = Math.round(seconds / 3600);
  return hours < 1 ? `${Math.round(seconds / 60)}m` : `${hours}h`;
}

export const uptime = (ms: number | undefined) => {
  if (!ms) return '-';

  const minutes = Math.floor(ms / 60000);
  return minutes < 60 ? `${minutes}m` : `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Session length in seconds → `45s`, `12m`, `2h 15m`. */
export const durationLabel = (seconds: number | undefined | null) => {
  if (!seconds || seconds < 0) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
